/*
PURPOSE: The SINGLE SOURCE OF TRUTH for the Coral CONTRIBUTION fee split — the corrected GST
model. A member's monthly contribution is a fixed gross price. It divides into exactly TWO
things the member bought: the artist contribution, and the Platform Fee. The Platform Fee is
the WHOLE amount wearecoral retains and is GST-INCLUSIVE, because wearecoral is PayPal's
merchant: payment processing is wearecoral's own cost inside a single taxable supply, not a
disbursement paid as the member's agent. Pure arithmetic, no I/O. Lives in the shared package
so checkout, the coral creator, settlement, history and the public export all compute from the
same code and can never drift.

USE CASES:
- Previewing the split for a pledge amount (checkout, the coral creator)
- Decomposing an already-settled contribution from its stored amounts (history, aggregates,
  the public export) WITHOUT recomputing it from a rate
- Supplying the three individual fee functions to the settlement layer, which owns artist
  allocation, pinning, and the rounding-remainder absorption that follows them

DOMAIN BOUNDARIES:
- Pure arithmetic; no DynamoDB, no PayPal, no I/O, no currency formatting
- One fee producer — downstream code reads stored or returned results, it does not recompute
- Integer cents throughout. Stored records hold DOLLARS (a legacy shape this correction does
  not change), so the read-time entry point converts once, at that boundary, with Math.round
- This producer is deliberately NOT wired into settlement. Settlement consumes the three fee
  functions individually, because the amount it retains is adjusted twice AFTER the fee maths
  runs (once for the per-artist rounding remainder, once for the pinned-division remainder),
  so a finished breakdown cannot exist at this producer's return on that path

ASSUMPTIONS:
- All monetary values are non-negative integer cents; a fractional cent is a caller bug
- GST is INCLUSIVE here, unlike the tickets booking fee where it is an on-top addend. It
  already sits inside the retained amount and is divided out, never added on
- GST is derived only from money that moved once wearecoral was registered for GST, so the
  read-time producers take the date of the money they decompose
- ROUNDING DIRECTION (a product decision, recorded here because this is where the split lives):
  GST rounds to nearest, and the Coral share absorbs the resulting remainder so the cost
  components always sum exactly back to the retained amount
- The artist share is the residual and absorbs processing variance, so the member's total never
  flexes. Pinning redistributes money AMONG artists and never changes the split itself
*/
/** The MODELLED payment-processing cost. An estimate for previews only: settlement uses the
 *  actual fee reported by the payment provider. */
export const PROCESSING_PERCENT = 0.029;
export const PROCESSING_FIXED_CENTS = 30;
/** wearecoral's own fee component, applied to the post-processing remainder. This is 7.75%
 *  ex-GST grossed up by 10% GST, which is why it is expressed inclusive. */
export const PLATFORM_FEE_INCL_GST_PERCENT = 0.08525;
/** The uplift a pinned artist receives, per pinned artist. Used by the settlement layer only. */
export const PINNED_ARTIST_PERCENT = 0.075;
/** Divisor that extracts GST from a GST-INCLUSIVE amount at a 10% rate. */
export const GST_INCLUSIVE_DIVISOR = 11;
/**
 * The date wearecoral Pty Ltd's GST registration runs from, per the ABN register. A contribution
 * dated before this carried no GST — an unregistered entity charges none — so none is derived
 * from it, and the Coral share is correspondingly larger.
 */
const GST_REGISTRATION_DATE = '20240701';
/**
 * Whether a contribution dated `contributionDate` falls in the GST-registered era.
 *
 * Money dates reach this in the two shapes the platform stores them in — YYYYMMDD and ISO-8601 —
 * so the comparison is on the calendar date as recorded. Registration is a calendar fact about
 * the entity, not an instant, and the boundary is nowhere near the affected money.
 */
export function contributionCarriesGst(contributionDate) {
    return calendarDigits(contributionDate) >= calendarDigits(GST_REGISTRATION_DATE);
}
/**
 * The leading YYYYMMDD of a date, whatever separators it was written with.
 *
 * An absent or unreadable date THROWS. It never falls back to an era: either default would be a
 * silent answer to a question about which side of a tax boundary real money sits on, and the
 * caller — which is reading a settled record that has a date — is the only party that can tell.
 */
function calendarDigits(date) {
    const digits = String(date !== null && date !== void 0 ? date : '').replace(/\D/g, '');
    if (digits.length < 8) {
        throw new Error(`coralContributionFee: contribution date must carry a full calendar date, got ${date}`);
    }
    return digits.slice(0, 8);
}
/** The modelled payment-processing cost for a contribution, in integer cents. */
export function modelledProcessingCents(totalCents) {
    return Math.round(totalCents * PROCESSING_PERCENT) + PROCESSING_FIXED_CENTS;
}
/** wearecoral's fee component on the post-processing remainder, in integer cents. */
export function platformFeeComponentCents(remainingCents) {
    return Math.round(remainingCents * PLATFORM_FEE_INCL_GST_PERCENT);
}
/**
 * The total pinned-artist uplift carved out of the artist pool, in integer cents. Exported for
 * the settlement layer, which owns pinning; it plays no part in the fee split, because the
 * uplift is returned to artists and so never changes what wearecoral retains.
 */
export function pinnedArtistsCents(baseCents, pinnedCount) {
    return Math.round(PINNED_ARTIST_PERCENT * baseCents * pinnedCount);
}
/**
 * The GST contained within a GST-inclusive retained amount. SINGLE SOURCE of the GST-base
 * decision: the base is the WHOLE retained amount, per the accounting determination that
 * processing is wearecoral's own cost inside one taxable supply, and it excludes money that
 * predates registration (see contributionCarriesGst). If either determination is ever revised,
 * narrow the base HERE, in one place.
 */
export function gstWithinRetained(retainedCents) {
    return Math.round(retainedCents / GST_INCLUSIVE_DIVISOR);
}
/**
 * Assemble and validate a breakdown from the two figures everything else derives from.
 * `gstBearingRetainedCents` is the part of the retained amount that carries GST: the whole of it
 * for money from the registered era, and less where a contribution (or part of an aggregated
 * span) predates registration.
 */
function buildBreakdown(totalCents, processingCents, retainedCents, gstBearingRetainedCents) {
    const gstCents = gstWithinRetained(gstBearingRetainedCents);
    const breakdown = {
        totalCents,
        artistShareCents: totalCents - retainedCents,
        retainedCents,
        processingCents,
        gstCents,
        // The residual, so the cost components always sum exactly back to the retained amount.
        coralShareCents: retainedCents - processingCents - gstCents,
    };
    assertInvariants(breakdown);
    return breakdown;
}
function zeroBreakdown() {
    return {
        totalCents: 0,
        artistShareCents: 0,
        retainedCents: 0,
        processingCents: 0,
        gstCents: 0,
        coralShareCents: 0,
    };
}
function assertIntegerCents(value, label) {
    if (!Number.isInteger(value) || value < 0) {
        throw new Error(`coralContributionFee: ${label} must be non-negative integer cents, got ${value}`);
    }
}
/**
 * PREVIEW producer. Computes the split from a pledge amount, using the modelled processing rate
 * unless an actual cost is supplied. Use this wherever the contribution has not settled yet.
 *
  * For an already-settled contribution use contributionBreakdownFromStored instead — reading the
 * stored amounts, rather than re-deriving them from a rate, is what keeps display figures
 * correct across a rate change.
 */
export function computeContributionFeeBreakdown(totalCents, opts = {}) {
    assertIntegerCents(totalCents, 'totalCents');
    if (totalCents === 0)
        return zeroBreakdown();
    const processingCents = opts.actualProcessingCents !== undefined
        ? opts.actualProcessingCents
        : modelledProcessingCents(totalCents);
    assertIntegerCents(processingCents, 'actualProcessingCents');
    const remainingCents = totalCents - processingCents;
    const retainedCents = processingCents + platformFeeComponentCents(remainingCents);
    return buildBreakdown(totalCents, processingCents, retainedCents, retainedCents);
}
/**
 * READ-TIME producer for an AGGREGATE, taking the components directly in integer cents.
 *
 * Use this when summing many contributions: accumulate each component in cents and pass them
 * here, rather than summing dollars and converting once. It also spares aggregate callers from
 * re-deriving retained and GST themselves, which would put a second copy of this arithmetic
 * outside the producer.
 */
export function contributionBreakdownFromComponentCents(components) {
    var _a;
    assertIntegerCents(components.artistShareCents, 'artistShareCents');
    assertIntegerCents(components.processingCents, 'processingCents');
    assertIntegerCents(components.platformFeeComponentCents, 'platformFeeComponentCents');
    const retainedCents = components.processingCents + components.platformFeeComponentCents;
    const preGstRegistrationRetainedCents = (_a = components.preGstRegistrationRetainedCents) !== null && _a !== void 0 ? _a : 0;
    assertIntegerCents(preGstRegistrationRetainedCents, 'preGstRegistrationRetainedCents');
    if (preGstRegistrationRetainedCents > retainedCents) {
        throw new Error(`coralContributionFee: preGstRegistrationRetainedCents (${preGstRegistrationRetainedCents}) exceeds the retained amount (${retainedCents}) it is carved from`);
    }
    const totalCents = components.artistShareCents + retainedCents;
    if (totalCents === 0)
        return zeroBreakdown();
    return buildBreakdown(totalCents, components.processingCents, retainedCents, retainedCents - preGstRegistrationRetainedCents);
}
/**
 * READ-TIME producer. Decomposes an already-settled contribution from the amounts settlement
 * stored, without recomputing anything from a rate. Stored money is in DOLLARS, so it is
 * converted once here with Math.round — verified lossless for every value the writer can
 * produce. Never truncate: truncation loses a cent on 383 of the 7,001 producible values.
 */
export function contributionBreakdownFromStored(stored) {
    const toCents = (dollars, label) => {
        if (!Number.isFinite(dollars)) {
            throw new Error(`coralContributionFee: ${label} must be a finite dollar amount, got ${dollars}`);
        }
        return Math.round(dollars * 100);
    };
    const totalCents = toCents(stored.pledgeAmount, 'pledgeAmount');
    const processingCents = toCents(stored.transactionFee, 'transactionFee');
    const retainedCents = processingCents + toCents(stored.platformFee, 'platformFee');
    if (totalCents === 0 && retainedCents === 0)
        return zeroBreakdown();
    const gstBearingRetainedCents = contributionCarriesGst(stored.contributionDate) ? retainedCents : 0;
    return buildBreakdown(totalCents, processingCents, retainedCents, gstBearingRetainedCents);
}
/**
 * Fail-loud reconciliation. A violation is a programming error or a corrupt record: money must
 * always balance, and a silent partial here would surface as a member-facing breakdown whose
 * components do not sum to what they paid.
 *
 *   artistShare + retained === total
 *   processing + gst + coralShare === retained
 *   every component >= 0
 */
function assertInvariants(b) {
    if (b.artistShareCents + b.retainedCents !== b.totalCents) {
        throw new Error(`coralContributionFee invariant: artistShare (${b.artistShareCents}) + retained (${b.retainedCents}) !== total (${b.totalCents})`);
    }
    if (b.processingCents + b.gstCents + b.coralShareCents !== b.retainedCents) {
        throw new Error(`coralContributionFee invariant: processing (${b.processingCents}) + gst (${b.gstCents}) + coralShare (${b.coralShareCents}) !== retained (${b.retainedCents})`);
    }
    const negative = Object.entries(b).find(([, value]) => value < 0);
    if (negative) {
        throw new Error(`coralContributionFee invariant: ${negative[0]} is negative (${negative[1]}) for a total of ${b.totalCents}`);
    }
}
